import { Injectable } from '@angular/core';
import { BUDGET_STAGES } from '@app/core';

@Injectable({
  providedIn: 'root',
})
export class OpportunityStageService {
  /**
   * Etapas estándar de oportunidades
   */
  readonly opportunityStages = [
    { name: 'Nuevo', order: 1 },
    { name: 'Evaluación', order: 2 },
    { name: 'Presupuesto', order: 3 },
    { name: 'Pausada', order: 4 },
    { name: 'Adjudicada', order: 5 },
    { name: 'Cancelada', order: 6 },
  ];

  /**
   * Actualiza las etapas disponibles basándose en el equipo de la oportunidad
   */
  updateStagesForTeam(opportunity: any, stages: any[]): any[] {
    if (!opportunity || !stages) return [];

    const team = opportunity.teamId;
    let stagesForTeam: any[] = [];

    if (!team) {
      // Si no hay equipo, usar las etapas estándar
      stagesForTeam = this.opportunityStages.map((s) => s.name);
    } else {
      // Filtrar etapas que pertenecen al equipo
      const teamStages = stages.filter((s: any) => {
        return (
          s.teams &&
          Array.isArray(s.teams) &&
          s.teams.some((t: any) => t.id === team)
        );
      });

      if (teamStages.length > 0) {
        stagesForTeam = teamStages.map((s: any) => s.name);
      } else {
        // Si no hay etapas específicas del equipo, usar las estándar
        stagesForTeam = this.opportunityStages.map((s) => s.name);
      }
    }

    // Mapear a objetos con orden e ID
    return stagesForTeam.map((stageName) => {
      const opportunityStage = this.opportunityStages.find(
        (os) => os.name === stageName
      );
      const backendStage = stages.find((s: any) => s.name === stageName);
      return {
        name: stageName,
        order: opportunityStage?.order || 0,
        id: backendStage?.id || null,
      };
    });
  }

  /**
   * Obtiene el nombre de la etapa actual de la oportunidad
   */
  getCurrentStageName(opportunity: any, stages: any[]): string {
    if (!opportunity?.stageId || !stages) return 'Nuevo';
    const stage = stages.find((s: any) => s.id === opportunity.stageId);
    return stage?.name || 'Nuevo';
  }

  /**
   * Verifica si una etapa es la etapa actual de la oportunidad
   */
  isCurrentStage(opportunity: any, stageName: string, stages: any[]): boolean {
    const currentStageName = this.getCurrentStageName(opportunity, stages);
    return currentStageName === stageName;
  }

  /**
   * Obtiene las etapas a mostrar para un presupuesto, incluyendo cancelado si aplica
   */
  getBudgetStagesToDisplay(budget: any): any[] {
    if (!budget) return [];

    const stages = BUDGET_STAGES.filter((stage) => stage.value !== 'Cancelled');
    if (budget.state === 'Cancelled') {
      const cancelledStage = BUDGET_STAGES.find((stage) => stage.value === 'Cancelled');
      return cancelledStage ? [...stages, cancelledStage] : [...stages];
    }
    return [...stages];
  }

  /**
   * Obtiene el nombre de la etapa actual del presupuesto
   */
  getCurrentBudgetStageName(budget: any): string {
    if (!budget) return 'Presupuesto';

    const stage = BUDGET_STAGES.find((s) => s.value === budget.state);
    return stage?.name || 'Presupuesto';
  }

  /**
   * Obtiene el valor de la etapa actual del presupuesto
   */
  getCurrentBudgetStageValue(budget: any): string {
    if (!budget?.state) return 'Budget';
    return budget.state;
  }

  /**
   * Verifica si una etapa es la etapa actual del presupuesto
   */
  isCurrentBudgetStage(budget: any, stageValue: string): boolean {
    return budget?.state === stageValue;
  }

  /**
   * Verifica si se puede crear un presupuesto basándose en la etapa actual
   */
  canCreateQuote(opportunity: any, stages: any[]): boolean {
    if (!opportunity || !stages) return false;

    const currentStage = this.getCurrentStageName(opportunity, stages);

    // Solo se puede crear presupuesto si no está ganada, perdida o cancelada
    const isLost = !!opportunity.dateLost;
    const isWon = !!opportunity.dateWon;
    const isCancelled = currentStage === 'Cancelada';

    return !isLost && !isWon && !isCancelled;
  }
}
